/** Easy onboarding (backend plan 2026-10-09-easy-onboarding-fareharbor §5.3, §5.4, §5.7–§5.9):
 *
 *  Pre-tenant (`console_top_manage` + `console_preflight_manage`):
 *  - `GET  /easy_onboarding/status/?expand=credential_fields` — can this environment run the
 *    flow at all, and which fields each booking system's credentials are pasted into?
 *  - `POST /easy_onboarding/{restech}/companies/ {credentials}` — the companies they reach.
 *  - `POST /easy_onboarding/{restech}/company/ {credentials, account}` — the confirm card.
 *  - `POST /easy_onboarding/{restech}/create/` — one click; 201 `{id, code}`.
 *
 *  Tenant-scoped:
 *  - `GET  /tour_operators/{id}/easy_onboarding/` — the aggregate state the screens and the
 *    tray poll (A5).
 *  - `POST …/selection/ {product_ids}`, `POST …/logo/` (JSON choice or multipart upload),
 *    `POST …/discovery/ {more, window_days?}` (202: a pass started) and `POST …/finish/`
 *    (no body: "Finish without tours").
 *  - `POST …/go_live/ {resync, launch_policy_id}` — go live: lift the outbound block and switch
 *    the default launch policy, now (200) or after the `easy_go_live` re-sync task (202).
 *
 *  THE CREDENTIALS. A booking system's keys (FareHarbor's user key, Bókun's access and secret
 *  key) travel only in a POST body, never in a URL, a query key, a mutation's cached
 *  variables, router state or storage. These functions take them as an argument and drop
 *  them; callers must call them directly (not through `useMutation`, whose cache keeps the
 *  variables) and never log the request.
 *
 *  Shapes come from the generated spec types; never hand-mirror them. Error-body fields are
 *  read in ONE place each below, so a rename is a one-line change.
 *
 *  EVERY ANSWER IS SHAPE-CHECKED. The backend's error page can answer 200 with HTML: axios
 *  then hands back a string, which a query would cache and a screen would crash on
 *  (`status.blockers.length`, `state.lanes.some`). Each call checks it got an object with the
 *  fields the screens dereference, and otherwise throws `badResponse` — a 5xx-like error, so
 *  the state poll backs off and retries, and nothing malformed is ever cached. */
import { apiGet, apiPost, ConsoleApiError } from './client';
import type { components } from './types';

type Schemas = components['schemas'];

export type EasyStatus = Schemas['ConsoleEasyOnboardingStatus'];
export type EasyRestechStatus = Schemas['ConsoleEasyOnboardingRestech'];
export type EasyCredentialField = Schemas['ConsoleEasyOnboardingCredentialField'];
/** `{<credential field name>: <value>}`: exactly the restech's `credential_fields`. */
export type EasyCredentials = Record<string, string>;
export type EasyIssue = Schemas['ConsoleEasyOnboardingIssue'];
export type EasyCompanies = Schemas['ConsoleEasyOnboardingCompanies'];
export type EasyCompanyRef = EasyCompanies['companies'][number];
export type EasyCompany = Schemas['ConsoleEasyOnboardingCompany'];
export type EasyCompanyFacts = Schemas['ConsoleEasyOnboardingCompanyFacts'];
export type EasyDerived = Schemas['ConsoleEasyOnboardingDerived'];
/** One tenant code suggestion (`derived.code_options`): the original first, then free ones. */
export type EasyCodeOption = Schemas['ConsoleEasyOnboardingCodeOption'];
export type EasyDuplicate = Schemas['ConsoleEasyOnboardingDuplicate'];
export type EasyCreateRequest = Schemas['ConsoleEasyOnboardingCreateRequest'];
/** The create body the console sends: always `credentials` + `account` (the spec still takes
 *  the older `user_key` / `shortname` from older callers). */
export type EasyCreateBody = Omit<
  EasyCreateRequest,
  'credentials' | 'account' | 'user_key' | 'shortname'
> & { credentials: EasyCredentials; account: string };
export type EasyCreated = Schemas['ConsoleEasyOnboardingCreated'];
export type EasyErrorCode = Schemas['ConsoleEasyOnboardingError']['code'];
export type EasyCodeTakenBody = Schemas['ConsoleEasyOnboardingCodeTaken'];
export type EasyState = Schemas['ConsoleEasyOnboarding'];
/** Ready's webhook instructions where the operator adds our webhook in its own booking system
 *  (`next_steps[webhook_request].instructions`, manual setup: Bókun). */
export type EasyWebhookInstructions = Schemas['ConsoleEasyOnboardingWebhookInstructions'];
export type EasyLane = Schemas['ConsoleEasyOnboardingLane'];
export type EasyLaneKey = EasyLane['key'];
export type EasyLaneState = EasyLane['state'];
export type EasyProduct = Schemas['ConsoleEasyOnboardingProduct'];
export type EasyPhase = EasyState['phase'];
export type EasySelectionResult = Schemas['ConsoleEasyOnboardingSelectionResult'];
export type EasyAttention = Schemas['ConsoleEasyOnboardingAttention'];
export type EasyDiscoveryRequest = Schemas['ConsoleEasyOnboardingDiscoveryRequest'];
export type EasyDiscoveryResult = Schemas['ConsoleEasyOnboardingDiscoveryResult'];
export type EasyFinishResult = Schemas['ConsoleEasyOnboardingFinishResult'];
export type EasyGoLive = Schemas['ConsoleEasyOnboardingGoLive'];
export type EasyGoLiveRequest = Schemas['ConsoleEasyOnboardingGoLiveRequest'];
export type EasyGoLiveResult = Schemas['ConsoleEasyOnboardingGoLiveResult'];
export type EasyLogoChoice = Schemas['ConsoleEasyOnboardingLogoChoice'];
export type EasyLogoResult = Schemas['ConsoleEasyOnboardingLogoResult'];
export type EasyLogoRefusalCode = Schemas['ConsoleEasyOnboardingLogoRefusal']['code'];
/** The `find_logo` bgtask's result (`GET /bgtasks/{id}/result/` → `result`); candidate images
 *  are bgtask artifacts streamed through `/bgtasks/{id}/artifact_image/` (`artifactImage`) —
 *  the original files as published (any format of four, any aspect ratio, possibly
 *  transparent). */
export type FindLogoResult = Schemas['ConsoleFindLogoResult'];
export type FindLogoCandidate = Schemas['ConsoleFindLogoCandidate'];

/** The restechs the easy endpoints take (the spec's `EasyOnboardingRestech`). */
export type EasyRestech = components['parameters']['EasyOnboardingRestech'];

/** Every typed refusal code the console keys off, named once. */
export const EASY_ERROR = {
  credentialRejected: 'credential_rejected',
  noCompanies: 'no_companies',
  appKeyMissing: 'app_key_missing',
  vendorUnreachable: 'vendor_unreachable',
  disabled: 'disabled',
  unknownRestech: 'unknown_restech',
  accountAmbiguous: 'account_ambiguous',
  accountNotFound: 'account_not_found',
  scopeMissing: 'bokun_scope_missing',
  notConnected: 'not_connected',
  codeTaken: 'code_taken',
  setupRunDisabled: 'setup_run_disabled',
  notEasy: 'not_easy',
  invalidSelection: 'invalid_selection',
  importRunning: 'import_running',
  alreadyReady: 'already_ready',
  discoveryRunning: 'discovery_running',
  discoveryBlocked: 'discovery_blocked',
  noMoreProducts: 'no_more_products',
  badPolicy: 'bad_policy',
  policyNotLive: 'policy_not_live',
  goLiveRunning: 'go_live_running',
  notBlocked: 'not_blocked',
} as const satisfies Record<string, EasyErrorCode>;

/** The logo override's refusal codes (`ConsoleEasyOnboardingLogoRefusal`): a candidate it
 *  cannot apply, or an uploaded file it will not store. */
export const EASY_LOGO_ERROR = {
  badCandidate: 'bad_candidate',
  candidateExpired: 'candidate_expired',
  candidateUnavailable: 'candidate_unavailable',
  imageType: 'image_type',
  imageTooLarge: 'image_too_large',
  imageUnreadable: 'image_unreadable',
  imageOrientation: 'image_orientation',
  imageEmpty: 'image_empty',
} as const satisfies Record<string, EasyLogoRefusalCode>;

/** Under the operator, so `['tourOperators']` invalidations reach it. */
export const easyStateKey = (topId: number) => ['tourOperators', topId, 'easyOnboarding'] as const;
export const easyStatusKey = ['easyOnboarding', 'status'] as const;

/** The status a malformed answer is reported with: a bad gateway (retryable, never a 4xx). */
export const BAD_RESPONSE_STATUS = 502;

/** A 200 that is not the JSON object the spec promises (the HTML error page, a proxy's text).
 *  A `ConsoleApiError` with a 5xx status and no body, so callers treat it as an opaque transient
 *  server failure (no typed code, no backend sentence). */
export function badResponse(what: string): ConsoleApiError {
  return new ConsoleApiError(
    BAD_RESPONSE_STATUS,
    undefined,
    `easy onboarding: ${what} is not the expected JSON object`,
  );
}

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);
const arrays = (...keys: string[]) => (o: Obj) => keys.every((k) => Array.isArray(o[k]));

/** `data` as `T` when it is an object passing `ok`, else throws `badResponse`. */
function shaped<T>(data: unknown, what: string, ok: (o: Obj) => boolean = () => true): T {
  if (!isObj(data) || !ok(data)) throw badResponse(what);
  return data as T;
}

/** The status with each restech's `credential_fields` (the Connect form). */
export async function getStatus(signal?: AbortSignal): Promise<EasyStatus> {
  return shaped<EasyStatus>(
    await apiGet<unknown>('/easy_onboarding/status/', {
      signal,
      params: { expand: 'credential_fields' },
    }),
    'the status',
    (o) => typeof o.enabled === 'boolean' && arrays('restechs', 'blockers', 'warnings')(o),
  );
}

/** The companies the credentials reach; each one's `shortname` is its account id. */
export async function listCompanies(
  restech: EasyRestech,
  credentials: EasyCredentials,
): Promise<EasyCompanies> {
  return shaped<EasyCompanies>(
    await apiPost<unknown>(`/easy_onboarding/${restech}/companies/`, { credentials }),
    'the company list',
    arrays('companies'),
  );
}

export async function getCompany(
  restech: EasyRestech,
  credentials: EasyCredentials,
  account: string,
): Promise<EasyCompany> {
  return shaped<EasyCompany>(
    await apiPost<unknown>(`/easy_onboarding/${restech}/company/`, { credentials, account }),
    'the company',
    (o) => isObj(o.facts) && isObj(o.derived) && Array.isArray(o.duplicates),
  );
}

export async function create(restech: EasyRestech, body: EasyCreateBody): Promise<EasyCreated> {
  // Without an id there is no tenant to open: fail here instead of navigating to
  // /tour-operators/undefined/onboard.
  return shaped<EasyCreated>(
    await apiPost<unknown>(`/easy_onboarding/${restech}/create/`, body),
    'the created tenant',
    (o) => typeof o.id === 'number',
  );
}

export async function getState(topId: number, signal?: AbortSignal): Promise<EasyState> {
  return shaped<EasyState>(
    await apiGet<unknown>(`/tour_operators/${topId}/easy_onboarding/`, { signal }),
    'the onboarding state',
    (o) =>
      typeof o.phase === 'string' &&
      arrays('lanes', 'attention', 'products', 'next_steps')(o) &&
      ['tour_operator', 'company', 'discovery', 'selection', 'results', 'go_live'].every((k) =>
        isObj(o[k]),
      ),
  );
}

export async function postSelection(
  topId: number,
  productIds: number[],
): Promise<EasySelectionResult> {
  return shaped<EasySelectionResult>(
    await apiPost<unknown>(`/tour_operators/${topId}/easy_onboarding/selection/`, {
      product_ids: productIds,
    }),
    'the selection answer',
  );
}

/** Starts a discovery pass: `{more: true}` the next one, `{more: false}` a rerun of the first,
 *  `window_days` a wider look ahead. 202 with the started task. */
export async function postDiscovery(
  topId: number,
  body: EasyDiscoveryRequest,
): Promise<EasyDiscoveryResult> {
  return shaped<EasyDiscoveryResult>(
    await apiPost<unknown>(`/tour_operators/${topId}/easy_onboarding/discovery/`, body),
    'the discovery answer',
  );
}

/** "Finish without tours": the flow moves to Ready; a selection stuck behind a failed
 *  prerequisite is unstaged and named in `dropped_selection`. */
export async function postFinish(topId: number): Promise<EasyFinishResult> {
  return shaped<EasyFinishResult>(
    await apiPost<unknown>(`/tour_operators/${topId}/easy_onboarding/finish/`),
    'the finish answer',
  );
}

/** Go live: `resync: false` lifts the outbound block and switches the default launch policy
 *  now (200); `resync: true` starts the `easy_go_live` background task that re-reads the sample
 *  bookings first (202, `task_id`); a tenant already live answers 200 `already: true`. */
export async function postGoLive(topId: number, body: EasyGoLiveRequest): Promise<EasyGoLiveResult> {
  return shaped<EasyGoLiveResult>(
    await apiPost<unknown>(`/tour_operators/${topId}/easy_onboarding/go_live/`, body),
    'the go live answer',
    (o) => typeof o.already === 'boolean' && isObj(o.go_live),
  );
}

/** The logo override: another candidate, no logo, or (multipart) an uploaded file. */
export async function postLogo(
  topId: number,
  choice: EasyLogoChoice | { upload: File },
): Promise<EasyLogoResult> {
  const url = `/tour_operators/${topId}/easy_onboarding/logo/`;
  let data: unknown;
  if ('upload' in choice) {
    const fd = new FormData();
    fd.append('file', choice.upload);
    // The client defaults to JSON, which would serialise the FormData as `{"file":{}}`: unset
    // the type so axios sends multipart with its own boundary (as every console upload does).
    data = await apiPost<unknown>(url, fd, { headers: { 'Content-Type': undefined } });
  } else {
    data = await apiPost<unknown>(url, choice);
  }
  return shaped<EasyLogoResult>(data, 'the logo answer');
}

/** The typed refusal's code, or null for anything else (a DRF 400, a network failure). */
export function easyErrorCode(e: unknown): EasyErrorCode | null {
  if (!(e instanceof ConsoleApiError)) return null;
  const code = e.body?.code;
  return typeof code === 'string' ? (code as EasyErrorCode) : null;
}

/** The logo override's refusal code, or null for anything else (DRF field errors, a network
 *  failure, a code this console does not know). */
export function easyLogoErrorCode(e: unknown): EasyLogoRefusalCode | null {
  if (!(e instanceof ConsoleApiError)) return null;
  const code = e.body?.code;
  return typeof code === 'string' && (Object.values(EASY_LOGO_ERROR) as string[]).includes(code)
    ? (code as EasyLogoRefusalCode)
    : null;
}

/** The sentence a refusal carries (`detail`), else a generic one. Never echoes a request. */
export function easyErrorDetail(e: unknown, fallback: string): string {
  if (e instanceof ConsoleApiError && typeof e.body?.detail === 'string' && e.body.detail)
    return e.body.detail;
  return fallback;
}

export interface CodeTaken {
  tourOperatorId: number | null;
  sameCompany: boolean;
}

/** 409 `code_taken` → the tenant holding the code (spec `ConsoleEasyOnboardingCodeTaken`).
 *  Its fields are read here and nowhere else. */
export function codeTakenOf(e: unknown): CodeTaken | null {
  if (
    !(e instanceof ConsoleApiError) ||
    e.status !== 409 ||
    easyErrorCode(e) !== EASY_ERROR.codeTaken
  )
    return null;
  const body = e.body as Partial<EasyCodeTakenBody> | undefined;
  const id = body?.tour_operator_id;
  return {
    tourOperatorId: typeof id === 'number' ? id : null,
    sameCompany: body?.same_company === true,
  };
}

/** Will setup apply this result's `chosen` without being asked? The backend says so
 *  (`auto_apply`); since setup always applies the best candidate, that is any result with a
 *  pick. A result from before the field existed reads as false. */
export function logoAutoApplies(r: FindLogoResult | undefined): boolean {
  return !!r && r.chosen != null && r.auto_apply === true;
}

const LOGO_CONTENT_TYPES: readonly string[] = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
] satisfies NonNullable<FindLogoCandidate['content_type']>[];

/** A `find_logo` task's result read defensively — a malformed one reads as "nothing found";
 *  a candidate without its artifact (no image to show) is dropped, an unknown `content_type`
 *  is left out. `low_confidence` is always set: a result from before the field existed is
 *  "not sure" unless setup applied its pick on its own (`auto_apply`), as it was then. */
export function parseFindLogoResult(raw: unknown): FindLogoResult {
  const r = (raw ?? {}) as Partial<FindLogoResult>;
  const candidates = Array.isArray(r.candidates)
    ? r.candidates
        .filter(
          (c): c is FindLogoCandidate =>
            !!c && typeof c === 'object' && typeof c.artifact === 'string',
        )
        .map(({ content_type, ...c }) =>
          typeof content_type === 'string' && LOGO_CONTENT_TYPES.includes(content_type)
            ? { ...c, content_type }
            : c,
        )
    : [];
  const chosen =
    typeof r.chosen === 'number' && r.chosen >= 0 && r.chosen < candidates.length ? r.chosen : null;
  const parsed: FindLogoResult = {
    ...r,
    chosen,
    confidence: typeof r.confidence === 'number' ? r.confidence : 0,
    auto_apply: r.auto_apply === true,
    candidates,
  };
  return {
    ...parsed,
    low_confidence:
      typeof r.low_confidence === 'boolean' ? r.low_confidence : !logoAutoApplies(parsed),
  };
}
